import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Sparkles, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useLoadout } from "@/context/LoadoutContext";
import Mascot from "@/components/Mascot";

/**
 * Full-screen celebratory overlay shown when the user crosses a level tier.
 * The `level` prop shape mirrors `data.level.current` from the gamification
 * summary: { key, idx, emoji }. Fires a multi-burst confetti when mounted.
 * Purely presentational — the parent `LevelUpWatcher` handles when to show
 * it and the localStorage bookkeeping.
 */
export default function LevelUpOverlay({ open, level, onClose }) {
  const { t } = useT();
  const { palette } = useLoadout();

  useEffect(() => {
    if (!open) return;
    // Multi-burst confetti — from bottom corners and centre for a big moment.
    const colors = [palette.accent, palette.accentSoft, "#FFD37E", "#FC9700", "#FFFFFF"];
    const shoot = (x, y, count) =>
      confetti({
        particleCount: count,
        spread: 90,
        origin: { x, y },
        colors,
        scalar: 1.2,
        startVelocity: 55,
      });
    shoot(0.5, 0.6, 180);
    setTimeout(() => shoot(0.2, 0.7, 120), 220);
    setTimeout(() => shoot(0.8, 0.7, 120), 440);
    setTimeout(() => shoot(0.5, 0.5, 120), 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!level) return null;

  const stateName = t(`rewards.states.${level.key}.name`) || level.key;
  const stateQuote = t(`rewards.states.${level.key}.quote`) || "";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="levelup-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", backgroundColor: "rgba(15,23,42,0.55)" }}
          onClick={onClose}
          data-testid="levelup-overlay"
        >
          <motion.div
            key="levelup-card"
            initial={{ scale: 0.7, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22, delay: 0.05 }}
            className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl text-white"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundImage: `linear-gradient(135deg, ${palette.from} 0%, ${palette.via} 50%, ${palette.to} 100%)` }}
          >
            {/* Ambient glow */}
            <div className="pointer-events-none absolute -top-20 -right-20 h-60 w-60 rounded-full blur-3xl" style={{ backgroundColor: palette.accent, opacity: 0.4 }} />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-60 w-60 rounded-full bg-white/15 blur-3xl" />

            <div className="relative px-6 py-8 md:px-10 md:py-12 text-center">
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.25, type: "spring", stiffness: 220, damping: 14 }}
                className="text-[10px] uppercase tracking-[0.35em] font-black inline-flex items-center gap-2"
                style={{ color: palette.accent }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t("rewards.levelUpTitle")}
                <Sparkles className="h-3.5 w-3.5" />
              </motion.div>

              <motion.div
                initial={{ scale: 0.2, opacity: 0, rotate: -25 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ delay: 0.35, type: "spring", stiffness: 180, damping: 12 }}
                className="mt-5 mx-auto flex items-center justify-center h-32 w-32 md:h-36 md:w-36 rounded-full bg-white/15 ring-4 shadow-2xl backdrop-blur relative"
                style={{ boxShadow: `0 0 60px -10px ${palette.accent}`, borderColor: palette.accent }}
                data-testid="levelup-emoji"
              >
                <span className="text-6xl md:text-7xl leading-none drop-shadow-lg">{level.emoji}</span>
                {/* Solano mascot in congratulating pose — leaps in from the
                    bottom-right of the badge ring to celebrate the level-up
                    with the user (same identity as the rest of the app). */}
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.6, rotate: 20 }}
                  animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                  transition={{ delay: 0.75, type: "spring", stiffness: 260, damping: 14 }}
                  className="absolute -bottom-6 -right-6 md:-bottom-8 md:-right-8"
                >
                  <Mascot size={72} pose="congratulating" alt="Solano" />
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.4 }}
                className="mt-5"
              >
                <div className="text-sm font-semibold opacity-80">{t("rewards.levelLabel")} {level.idx}</div>
                <h2 className="mt-1 font-heading text-3xl md:text-4xl font-bold" data-testid="levelup-name">
                  {stateName}
                </h2>
                {stateQuote && (
                  <p className="mt-3 text-sm md:text-base italic opacity-90 max-w-sm mx-auto leading-relaxed">
                    "{stateQuote}"
                  </p>
                )}
              </motion.div>

              <motion.button
                type="button"
                onClick={onClose}
                data-testid="levelup-continue"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.85, duration: 0.35 }}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/95 hover:bg-white text-slate-900 px-6 py-3 text-sm font-bold shadow-lg active:scale-95 transition"
              >
                {t("rewards.levelUpContinue")}
                <ArrowRight className="h-4 w-4" />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
